/**
 * General-purpose compact / percentage number formatting — the single source of
 * truth shared by the web frontend and the mobile app (mirrors the
 * `./outcome-colors` shared module).
 *
 * Pure, platform-neutral, Hermes-safe: only `Intl.NumberFormat`, `Math`,
 * `Number.prototype.toFixed`, and string ops (no `Array.prototype.toSorted`,
 * no DOM/RN APIs). Each platform re-exports this from its own
 * `lib/format/format-number` so existing call sites stay unchanged.
 *
 * Converts raw numbers into human-readable short forms:
 *   999           → "999"
 *   1_200         → "1.2K"
 *   52_000        → "52K"
 *   1_500_000     → "1.5M"
 *   2_300_000_000 → "2.3B"
 *   7_000_000_000_000 → "7T"
 *
 * Strips trailing zeros after the decimal (e.g. "1.0K" → "1K"). Handles
 * negatives. Returns "0" for zero.
 *
 * Currency/price formatting is deliberately NOT here — it is locale + exchange
 * rate + currency-store coupled, so it stays platform-local
 * (`useFormattedCurrency` / `format-d6`).
 */

/** Locale used when a caller does not supply one (SSR / non-hook paths). */
const DEFAULT_LOCALE = 'en-US';

const pctFormatterByLocale: Map<string, Intl.NumberFormat> = new Map<string, Intl.NumberFormat>();

/**
 * Format a percentage magnitude (WITHOUT the `%` sign) with locale grouping and
 * decimal mark, at most one fraction digit (`12.5` → `"12.5"` / German `"12,5"`).
 * The caller appends the literal `%`. Per-locale `Intl.NumberFormat` instances
 * are cached so identity stays stable across renders.
 */
export function formatPercentValue(
  value: number,
  locale: string = DEFAULT_LOCALE,
  minimumFractionDigits: 0 | 1 = 0,
): string {
  const cacheKey = `${locale}:${String(minimumFractionDigits)}`;
  let fmt = pctFormatterByLocale.get(cacheKey);
  if (fmt === undefined) {
    fmt = new Intl.NumberFormat(locale, { maximumFractionDigits: 1, minimumFractionDigits });
    pctFormatterByLocale.set(cacheKey, fmt);
  }
  return fmt.format(value);
}

interface CompactTier {
  threshold: number;
  suffix: string;
}

const TIERS: readonly CompactTier[] = [
  { threshold: 1_000_000_000_000, suffix: 'T' },
  { threshold: 1_000_000_000, suffix: 'B' },
  { threshold: 1_000_000, suffix: 'M' },
  { threshold: 1_000, suffix: 'K' },
];

/**
 * Format a number into a compact human-readable string.
 *
 * @param value - The number to format
 * @param precision - Maximum decimal places (default 1)
 * @returns Compact string like "52K", "1.5M", "2.3B"
 */
export function formatCompactNumber(value: number, precision: number = 1): string {
  if (value === 0) return '0';

  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);

  for (const tier of TIERS) {
    if (abs >= tier.threshold) {
      const scaled = abs / tier.threshold;
      const trimmed = stripFractionalZeros(scaled.toFixed(precision));
      return `${sign}${trimmed}${tier.suffix}`;
    }
  }

  // Below 1K — show as-is, respecting precision for decimals
  if (abs % 1 !== 0) {
    const trimmed = stripFractionalZeros(abs.toFixed(precision));
    return `${sign}${trimmed}`;
  }

  return `${sign}${String(abs)}`;
}

/**
 * Strip trailing zeros from the fractional part only: "1.50" → "1.5", "1.0" → "1".
 * Integer-only strings (e.g. "120" from `toFixed(0)`) are returned untouched — a
 * naive `/\.?0+$/` would wrongly eat their trailing zeros ("120" → "12").
 */
function stripFractionalZeros(formatted: string): string {
  if (!formatted.includes('.')) return formatted;
  return formatted.replace(/0+$/, '').replace(/\.$/, '');
}

/** Integer part of a 2-decimal percentage (`20.07` → `"20"`). */
export function percentIntPart(v: number): string {
  return v.toFixed(2).split('.')[0] ?? '0';
}

/** Fractional part of a 2-decimal percentage, dot-prefixed (`20.07` → `".07"`). */
export function percentFracPart(v: number): string {
  return `.${v.toFixed(2).split('.')[1] ?? '00'}`;
}
